/**
 * Print an order receipt on the kiosk's printer.
 *
 * Two printers are supported, chosen by the `kiosk_printer_mode` setting:
 *
 *  - 'windows' (default) — any printer installed in Windows. The kiosk runs
 *    Chrome with `--kiosk --kiosk-printing`, so `print()` sends the page
 *    straight to the default printer with no dialog. We render the receipt into
 *    a hidden, off-screen iframe (its own document, with @page sized for an
 *    80mm roll) and print only that — leaving the visible kiosk UI untouched.
 *
 *  - 'sk1_bridge' — the built-in Datecs SK1-31 kiosk printer, driven as a raw
 *    ESC/POS device by the local bridge agent (the same one that runs the card
 *    terminal). We POST the receipt data and the bridge renders and cuts it. No
 *    Windows driver, no print dialog, and the bridge can report paper-out.
 *
 * Best-effort in both modes: a printing failure must never block the payment
 * result flow, so the returned promise always resolves and never rejects.
 */

import axios from 'axios';
import { API_URL, DATECS_BRIDGE_URL } from '../../constants';
import Auth from '../../utils/Auth';
import Utils from '../../utils/Utils';
import { kioskFiscalEnabled, maybePrintFiscalReceipt } from './fiscalReceipt';

const T = (translations, key, fallback) => {
  const m = translations && translations.messages && translations.messages.datecs;
  return (m && m[key]) || fallback;
};

// Whether the kiosk should auto-print order receipts (per-place setting).
export function kioskReceiptPrintingEnabled() {
  return String(Utils.getSetting('kiosk_auto_print_receipt')) === '1';
}

/**
 * Text printed only on an UNPAID slip, under the "ЗА ПЛАЩАНЕ" box - where the
 * customer has to go and settle up ("Плащането се извършва вътре в ресторанта").
 * Per-place, because every venue is laid out differently; empty prints nothing.
 */
function unpaidNote() {
  const v = Utils.getSetting('kiosk_receipt_unpaid_note');
  return v == null ? '' : String(v).trim();
}

/**
 * Which printer this kiosk uses. Unknown values fall back to 'windows' so a
 * typo in the setting can never leave a kiosk unable to print at all.
 */
export function kioskPrinterMode() {
  const v = String(Utils.getSetting('kiosk_printer_mode') || '').toLowerCase();
  return v === 'sk1_bridge' ? 'sk1_bridge' : 'windows';
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function money(n, currency) {
  const v = Number(n);
  return (Number.isFinite(v) ? v.toFixed(2) : '0.00') + ' ' + (currency || '€');
}

function formatDate(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    const p = (x) => String(x).padStart(2, '0');
    return (
      p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear() +
      ' ' + p(d.getHours()) + ':' + p(d.getMinutes())
    );
  } catch (e) {
    return '';
  }
}

function buildHtml(receipt, translations) {
  const t = (k, f) => T(translations, k, f);
  const r = receipt || {};
  const items = Array.isArray(r.items) ? r.items : [];

  // Конфигурируем хоризонтален падинг (px) — за подравняване на печата.
  const parsePad = (name, def) => {
    const v = Utils.getSetting(name);
    const n = parseInt(v);
    return (v !== undefined && v !== null && v !== '' && !isNaN(n)) ? n : def;
  };
  const padLeft = parsePad('kiosk_receipt_padding_left', 4);
  const padRight = parsePad('kiosk_receipt_padding_right', 4);

  const rows = items
    .map((it) => {
      const qty = it.quantity || 1;
      const name = (it.isAddon ? '+ ' : '') + esc(it.name || '') +
        (it.size ? ' (' + esc(it.size) + ')' : '');

      // Избраните опции ("Протеин: Пилешко бон филе") излизат като коментар под реда.
      // Цената им вече е включена в цената на реда, затова тук няма отделна сума.
      const options = Array.isArray(it.options) && it.options.length > 0
        ? '<div class="opts">' + it.options.map(esc).join('<br/>') + '</div>'
        : '';

      return (
        '<tr>' +
        '<td class="q">' + qty + 'x</td>' +
        '<td class="n">' + name + options + '</td>' +
        '<td class="p">' + esc(money(it.price, r.currency)) + '</td>' +
        '</tr>'
      );
    })
    .join('');

  const paidLabel = r.paid
    ? t('receipt_paid', 'ПЛАТЕНО')
    : t('receipt_unpaid', 'ЗА ПЛАЩАНЕ');

  // Human label for the payment method (Bulgarian fallbacks).
  const methodLabels = {
    card: t('pm_card', 'Карта'),
    cash: t('pm_cash', 'В брой'),
    online: t('pm_online', 'Онлайн'),
    on_room_bill: t('pm_room', 'Към сметка на стая'),
    epay: t('pm_epay', 'ePay'),
  };
  const methodLabel = r.paymentMethod
    ? (methodLabels[r.paymentMethod] || r.paymentMethod)
    : '';
  const methodLine = methodLabel
    ? '<table><tr><td class="n">' + t('receipt_method', 'Начин на плащане') + '</td>' +
      '<td class="p">' + esc(methodLabel) + '</td></tr></table>'
    : '';

  const cardLine =
    r.card && (r.card.maskedPan || r.card.authCode || r.card.rrn)
      ? '<div class="card">' +
        (r.card.maskedPan ? esc(r.card.maskedPan) + '<br/>' : '') +
        (r.card.authCode ? t('receipt_auth', 'Авт. код') + ': ' + esc(r.card.authCode) + '<br/>' : '') +
        (r.card.rrn ? 'RRN: ' + esc(r.card.rrn) : '') +
        '</div>'
      : '';

  return (
    '<!doctype html><html><head><meta charset="utf-8"/>' +
    '<style>' +
    '@page { size: 80mm auto; margin: 0; }' +
    'html,body { margin:0; padding:0; }' +
    'body { width:72mm; margin:0 auto; padding:6px ' + padRight + 'px 18px ' + padLeft + 'px;' +
    ' font-family:"Courier New",monospace; color:#000; font-size:14px; font-weight:bold; line-height:1.4; }' +
    '.center { text-align:center; }' +
    '.place { font-size:15px; font-weight:bold; }' +
    '.ordno { font-size:22px; font-weight:bold; margin:6px 0; }' +
    '.muted { font-size:11px; }' +
    '.opts { font-size:11px; font-weight:normal; padding-left:2px; }' +
    'hr { border:none; border-top:1px dashed #000; margin:6px 0; }' +
    'table { width:100%; border-collapse:collapse; }' +
    'td { vertical-align:top; padding:1px 0; }' +
    'td.q { width:26px; }' +
    'td.p { text-align:right; white-space:nowrap; padding-left:6px; }' +
    '.total { font-size:15px; font-weight:bold; }' +
    '.total td { padding-top:4px; }' +
    '.status { text-align:center; font-size:15px; font-weight:bold;' +
    ' border:2px solid #000; padding:5px; margin:8px 0; }' +
    '.card { font-size:11px; text-align:center; margin-top:6px; }' +
    '.unpaid-note { text-align:center; font-size:13px; font-weight:bold; margin-top:6px; }' +
    '</style></head><body>' +
    '<div class="center place">' + esc(r.placeName || '') + '</div>' +
    '<div class="center ordno">' + t('receipt_order_no', 'Поръчка №') + ' ' + esc(r.orderNumber || '') + '</div>' +
    '<div class="center muted">' + esc(formatDate(r.dateISO)) + '</div>' +
    '<hr/>' +
    '<table>' + rows + '</table>' +
    '<hr/>' +
    '<table class="total"><tr><td class="n">' + t('receipt_total', 'ОБЩО') + '</td>' +
    '<td class="p">' + esc(money(r.total, r.currency)) + '</td></tr></table>' +
    methodLine +
    '<div class="status">' + esc(paidLabel) + '</div>' +
    (!r.paid && unpaidNote()
      ? '<div class="unpaid-note">' + esc(unpaidNote()) + '</div>'
      : '') +
    cardLine +
    '</body></html>'
  );
}

// End-of-Day settlement slip (80mm), matching the receipt style.
function buildEodHtml(info, translations) {
  const t = (k, f) => T(translations, k, f);
  const term = info.terminal || {};
  const resultLabel = info.ok
    ? t('eod_ok', 'ПРИКЛЮЧВАНЕТО УСПЕШНО')
    : t('eod_fail', 'ГРЕШКА ПРИ ПРИКЛЮЧВАНЕ');
  const line = (label, val) =>
    val != null && val !== ''
      ? '<table><tr><td class="n">' + esc(label) + '</td><td class="p">' + esc(val) + '</td></tr></table>'
      : '';
  return (
    '<!doctype html><html><head><meta charset="utf-8"/>' +
    '<style>' +
    '@page { size: 80mm auto; margin: 0; }' +
    'html,body { margin:0; padding:0; }' +
    'body { width:72mm; margin:0 auto; padding:6px 4px 18px 4px;' +
    ' font-family:"Courier New",monospace; color:#000; font-size:14px; font-weight:bold; line-height:1.4; }' +
    '.center { text-align:center; }' +
    '.place { font-size:15px; font-weight:bold; }' +
    '.title { font-size:18px; font-weight:bold; margin:6px 0; }' +
    '.muted { font-size:11px; }' +
    '.opts { font-size:11px; font-weight:normal; padding-left:2px; }' +
    'hr { border:none; border-top:1px dashed #000; margin:6px 0; }' +
    'table { width:100%; border-collapse:collapse; }' +
    'td { vertical-align:top; padding:1px 0; }' +
    'td.p { text-align:right; white-space:nowrap; padding-left:6px; }' +
    '.status { text-align:center; font-size:15px; font-weight:bold;' +
    ' border:2px solid #000; padding:5px; margin:8px 0; }' +
    '</style></head><body>' +
    '<div class="center place">' + esc(info.placeName || '') + '</div>' +
    '<div class="center title">' + t('eod_title', 'ДНЕВЕН ОТЧЕТ / ПРИКЛЮЧВАНЕ') + '</div>' +
    '<div class="center muted">' + esc(formatDate(info.dateISO)) + '</div>' +
    '<hr/>' +
    line(t('eod_terminal', 'Терминал'), term.tid) +
    line(t('eod_serial', 'Сериен №'), term.serial) +
    line(t('eod_batch', 'Партида'), term.batch) +
    '<div class="status">' + esc(resultLabel) + '</div>' +
    '</body></html>'
  );
}

// DEV-only helper: call window.__printTestReceipt() from the browser console to
// see the print dialog/receipt without running a real payment. Remove before
// shipping if undesired.
if (typeof window !== 'undefined') {
  window.__printTestReceipt = () =>
    printOrderReceipt({
      orderNumber: 'TEST-1',
      placeName: 'Тест заведение',
      items: [
        { name: 'Кафе еспресо', quantity: 2, price: 5.0 },
        { name: 'Сандвич', size: 'голям', quantity: 1, price: 6.5 },
        { name: 'Екстра сирене', quantity: 1, price: 1.0, isAddon: true },
      ],
      total: 12.5,
      currency: '€',
      paid: true,
      dateISO: new Date().toISOString(),
    });
}

/**
 * Labels for the SK1 bridge. The bridge has its own Bulgarian defaults, but we
 * always send the resolved strings so both printing modes read identically and
 * a translated kiosk prints in its own language.
 */
function bridgeLabels(translations) {
  const t = (k, f) => T(translations, k, f);
  return {
    order_no: t('receipt_order_no', 'Поръчка №'),
    total: t('receipt_total', 'ОБЩО'),
    method: t('receipt_method', 'Начин на плащане'),
    paid: t('receipt_paid', 'ПЛАТЕНО'),
    unpaid: t('receipt_unpaid', 'ЗА ПЛАЩАНЕ'),
    auth: t('receipt_auth', 'Авт. код'),
    pm_card: t('pm_card', 'Карта'),
    pm_cash: t('pm_cash', 'В брой'),
    pm_online: t('pm_online', 'Онлайн'),
    pm_room: t('pm_room', 'Към сметка на стая'),
    pm_epay: t('pm_epay', 'ePay'),
    eod_title: t('eod_title', 'ДНЕВЕН ОТЧЕТ / ПРИКЛЮЧВАНЕ'),
    eod_ok: t('eod_ok', 'ПРИКЛЮЧВАНЕТО УСПЕШНО'),
    eod_fail: t('eod_fail', 'ГРЕШКА ПРИ ПРИКЛЮЧВАНЕ'),
    eod_terminal: t('eod_terminal', 'Терминал'),
    eod_serial: t('eod_serial', 'Сериен №'),
    eod_batch: t('eod_batch', 'Партида'),
  };
}

// The bridge prints synchronously, so a job answers in well under a second;
// this ceiling only exists so a wedged agent can't hang the payment screen.
const BRIDGE_PRINT_TIMEOUT_MS = 8000;

// Last printer state we told the server about. Only changes are reported, so a
// kiosk printing all evening on a near-empty roll sends one message, not fifty
// — and the recovery back to `ok` is reported too, which is what clears the
// indicator in the admin.
let lastReportedPrinterState = null;

/**
 * Forward the printer's state to the backend, which decides whether it is worth
 * emailing the staff about. Fire-and-forget: this must never delay or fail a
 * print, so every error is swallowed.
 *
 * @param {object} status  the `status` object the bridge returns with each job
 */
function reportPrinterState(status) {
  try {
    if (!status || !status.known || !status.state) return;
    if (status.state === lastReportedPrinterState) return;

    const placeId = Auth.getOpenedPlace ? Auth.getOpenedPlace() : null;
    if (!placeId) return;

    // Set before the request, not after: a printer that flaps must not queue up
    // one in-flight report per print job.
    lastReportedPrinterState = status.state;

    fetch(API_URL + 'kiosk/printer-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placeId: placeId, status: status }),
    }).catch(() => {
      // Server unreachable — let the next state change try again.
      lastReportedPrinterState = null;
    });
  } catch (e) {
    /* reporting is never worth breaking a print over */
  }
}

/**
 * Hand a print job to the local bridge. Best-effort like every other path here:
 * resolves even when the bridge is down, and logs so a kiosk that silently
 * stops printing can still be diagnosed from the console.
 */
function printViaBridge(path, payload) {
  return new Promise((resolve) => {
    let settled = false;
    const done = (info) => {
      if (settled) return;
      settled = true;
      if (info) console.warn('[kiosk-print] ' + info);
      resolve();
    };
    const timer = setTimeout(() => done('bridge did not answer in time'), BRIDGE_PRINT_TIMEOUT_MS);

    fetch(DATECS_BRIDGE_URL + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })).catch(() => ({ ok: r.ok, d: null })))
      .then(({ ok, d }) => {
        clearTimeout(timer);
        // Both the 200 and the 409 body carry the printer's state, and the 409
        // is precisely where an empty roll shows up — so report before
        // branching on success.
        if (d && d.status) reportPrinterState(d.status);
        if (!ok || !d || d.ok !== true) {
          done('printer refused the job: ' + JSON.stringify(d));
          return;
        }
        if (d.status && d.status.paperNearEnd) {
          console.warn('[kiosk-print] paper is running out');
        }
        done();
      })
      .catch(() => {
        clearTimeout(timer);
        done('bridge unreachable');
      });
  });
}

/**
 * @param {object} receipt    receipt data from the confirm endpoint
 * @param {object} [translations]
 * @returns {Promise<void>} always resolves (best-effort)
 */
export function printOrderReceipt(receipt, translations) {
  if (!receipt) return Promise.resolve();
  if (kioskPrinterMode() === 'sk1_bridge') {
    return printViaBridge('/print', {
      receipt,
      labels: bridgeLabels(translations),
      unpaidNote: unpaidNote(),
    });
  }
  return printHtmlInIframe(buildHtml(receipt, translations));
}

/**
 * Print an End-of-Day settlement slip on the kiosk printer.
 * @param {object} info  { placeName, ok, status, terminal:{tid,serial,batch}, dateISO }
 * @param {object} [translations]
 * @returns {Promise<void>} always resolves (best-effort)
 */
export function printEndOfDayReport(info, translations) {
  if (kioskPrinterMode() === 'sk1_bridge') {
    return printViaBridge('/print/end-of-day', {
      info: info || {},
      labels: bridgeLabels(translations),
    });
  }
  return printHtmlInIframe(buildEodHtml(info || {}, translations));
}

// Shared 80mm-roll iframe printer used by both receipt and EOD slips.
function printHtmlInIframe(html) {
  return new Promise((resolve) => {
    if (!html) return resolve();

    let done = false;
    let iframe = null;
    const finish = () => {
      if (done) return;
      done = true;
      // Leave the iframe long enough for the print job to spool, then clean up.
      setTimeout(() => {
        if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1500);
      resolve();
    };

    try {
      iframe = document.createElement('iframe');
      Object.assign(iframe.style, {
        position: 'fixed',
        right: '0',
        bottom: '0',
        width: '0',
        height: '0',
        border: '0',
        visibility: 'hidden',
      });
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();

      const triggerPrint = () => {
        try {
          const w = iframe.contentWindow;
          if (w.matchMedia) {
            const mql = w.matchMedia('print');
            if (mql && mql.addListener) {
              mql.addListener((m) => { if (!m.matches) finish(); });
            }
          }
          w.onafterprint = finish;
          w.focus();
          w.print();
          // Fallback in case onafterprint never fires (kiosk-printing).
          setTimeout(finish, 4000);
        } catch (e) {
          finish();
        }
      };

      // Give the iframe document a tick to lay out before printing.
      if (iframe.contentWindow.document.readyState === 'complete') {
        setTimeout(triggerPrint, 50);
      } else {
        iframe.onload = () => setTimeout(triggerPrint, 50);
        setTimeout(triggerPrint, 600); // safety net if onload doesn't fire
      }
    } catch (e) {
      finish();
    }
  });
}

/**
 * Fetch an order's receipt from the backend and print it — but only when the
 * kiosk is in kiosk mode AND the `kiosk_auto_print_receipt` setting is on.
 * Best-effort and always resolves; safe to call after any order.
 *
 * @param {string} orderUuid
 * @param {object} [translations]
 * @returns {Promise<void>}
 */
export function maybePrintKioskReceipt(orderUuid, translations) {
  try {
    if (!orderUuid) return Promise.resolve();
    if (!Auth.isKioskMode()) return Promise.resolve();

    // The two printers are independent settings — an object may run either, both
    // or neither. One fetch feeds both.
    const wantSlip = kioskReceiptPrintingEnabled();
    const wantFiscal = kioskFiscalEnabled();
    if (!wantSlip && !wantFiscal) return Promise.resolve();

    return axios
      .get(API_URL + 'orders/' + encodeURIComponent(orderUuid) + '/kiosk-receipt')
      .then((res) => {
        const receipt = res && res.data && res.data.success ? res.data.receipt : null;
        if (!receipt) return;
        // Fiscal first: it is the legal document, the slip is a courtesy copy.
        return maybePrintFiscalReceipt(receipt, orderUuid).then(() =>
          wantSlip ? printOrderReceipt(receipt, translations) : undefined
        );
      })
      .catch(() => {});
  } catch (e) {
    return Promise.resolve();
  }
}
